import React from "react";

export default function VisualizerEntry({
  brandLogoUrl,
  brandName,
  handleUpload,
  handleDemoSelect,
  demoRooms = [],
  room4,
  loading = false,
  PROCESSING_MESSAGES = [],
  processingMessageIndex = 0,
  DemoRoomCard,
}) {
  return (
    <div className="min-h-screen bg-white px-6 py-8 md:px-10 lg:px-16">
      <div className="mx-auto w-full max-w-[1680px]">
        {/* Top Section: Hero & Upload */}
        <div className="grid grid-cols-1 lg:grid-cols-[0.52fr_0.48fr] gap-10 lg:gap-16 items-start">
          <section className="pt-4">
            {brandLogoUrl && (
              <img
                src={brandLogoUrl}
                alt={brandName || "Company logo"}
                className="h-16 max-w-48 object-contain mb-8"
              />
            )}

            <h1 className="text-4xl md:text-5xl font-semibold text-gray-800 mb-8">
              Select a room for Transformation
            </h1>

            <div className="space-y-4 mb-8 text-lg text-gray-800">
              <div className="flex items-center gap-3">
                <svg
                  className="w-6 h-6 text-gray-900"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M9 3 7.17 5H4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-3.17L15 3H9Zm3 15a5 5 0 1 1 0-10 5 5 0 0 1 0 10Zm0-2a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                </svg>
                <span>Upload a picture of your room</span>
              </div>
              <div className="flex items-center gap-3">
                <svg
                  className="w-6 h-6 text-gray-900"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m21 16-9 5-9-5m18-5-9 5-9-5m18-5-9 5-9-5 9-5 9 5Z"
                  />
                </svg>
                <span>Try our products in your room</span>
              </div>
            </div>

            <label className="cursor-pointer block max-w-xl">
              <div className="w-full px-8 py-5 bg-gray-900 hover:bg-gray-800 rounded-lg text-white text-lg font-semibold transition-colors flex items-center justify-center gap-3 shadow-sm">
                <svg
                  className="w-7 h-7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v1.75A2.75 2.75 0 0 0 5.75 21h12.5A2.75 2.75 0 0 0 21 18.25V16.5M7 10l5-5m0 0 5 5m-5-5v12"
                  />
                </svg>
                Upload
              </div>

              <input
                type="file"
                accept="image/*"
                onChange={handleUpload}
                className="hidden"
              />
            </label>
          </section>

          {/* Hero Banner Preview Image */}
          <section className="rounded-lg overflow-hidden bg-gray-100 min-h-[300px] lg:min-h-[380px]">
            <img
              src={room4}
              alt="Room visualizer preview"
              className="w-full h-full min-h-[300px] lg:min-h-[380px] object-cover"
            />
          </section>
        </div>

        {/* Demo Rooms Section */}
        <section className="mt-12">
          <h2 className="text-2xl md:text-3xl font-semibold text-gray-700 mb-6">
            No picture? Try our demo rooms instead
          </h2>

          <div className="rounded-lg bg-gray-50 border border-gray-100 p-4 md:p-6">
            <div className="flex items-center gap-4 border-b border-gray-200 mb-6 overflow-x-auto">
              <button className="px-6 py-3 text-base font-semibold text-gray-900 border-b-2 border-gray-900">
                2D
              </button>
              <button className="px-6 py-3 text-base font-semibold text-gray-500 hover:text-gray-900 transition-colors">
                Panorama
              </button>
              <button className="px-6 py-3 text-base font-semibold text-gray-500 hover:text-gray-900 transition-colors">
                My Rooms
              </button>
            </div>

            {/* Room Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
              {demoRooms.map((room) => (
                <DemoRoomCard
                  key={room.roomId}
                  image={room.image}
                  name={room.name}
                  roomId={room.roomId}
                  onDemoSelect={handleDemoSelect}
                />
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Loading Modal Backdrop */}
      {loading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/55 px-6 backdrop-blur-md">
          <div className="w-full max-w-md rounded-2xl border border-teal-100 bg-white/95 p-8 shadow-2xl">
            <div className="mx-auto mb-6 flex h-28 w-28 items-center justify-center rounded-full bg-gradient-to-br from-teal-50 via-white to-amber-50">
              <div className="relative h-20 w-20">
                <div className="absolute inset-0 rounded-full border-[6px] border-teal-100" />
                <div className="absolute inset-0 animate-spin rounded-full border-[6px] border-transparent border-t-teal-600 border-r-amber-400" />
                <div className="absolute inset-4 rounded-lg border-2 border-teal-500 bg-white shadow-inner">
                  <div className="absolute left-2 right-2 top-3 h-2 rounded bg-amber-200" />
                  <div className="absolute bottom-3 left-2 h-7 w-3 rounded-sm bg-teal-200" />
                  <div className="absolute bottom-3 right-2 h-7 w-3 rounded-sm bg-slate-200" />
                </div>
              </div>
            </div>

            <div className="text-center">
              <div className="text-xl font-semibold text-gray-900">
                {PROCESSING_MESSAGES[processingMessageIndex]}
              </div>
              <p className="mt-2 text-sm text-gray-600">
                We are preparing your room so products fit the right surfaces.
              </p>
            </div>

            <div className="mt-6 flex items-center justify-center gap-2">
              {PROCESSING_MESSAGES.map((message, index) => (
                <span
                  key={message}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === processingMessageIndex
                      ? "w-8 bg-teal-600"
                      : "w-2 bg-teal-100"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}